// src/pages/ConsultantTeamHub.js
// A single hub page for a consultant's view of one team.
// Tabs: Meetings | Observations | Team Assessment | Working Genius | Manage Team
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  collection, query, where, getDocs, doc, getDoc, addDoc, serverTimestamp,
  updateDoc, arrayUnion, arrayRemove,
} from 'firebase/firestore/lite';
import { httpsCallable } from 'firebase/functions';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell, Legend,
} from 'recharts';
import { scoreInventoryFromChecks, getMeetingDurationMs } from '../utils/metrics';
import { db, functions } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { formatDuration } from '../utils/shortcodes';
import { DEFAULT_CATEGORIES, SCALE_LABELS, withCurrentLabels } from '../utils/surveyQuestions';
import TeamAssessment from './TeamAssessment';
import PageLoading from '../components/PageLoading';

const GC = '#5f7942';
const FC = '#922b3f';
const WG_TYPES = [
  { key: 'W', label: 'Wonder'      },
  { key: 'I', label: 'Invention'   },
  { key: 'D', label: 'Discernment' },
  { key: 'G', label: 'Galvanizing' },
  { key: 'E', label: 'Enablement'  },
  { key: 'T', label: 'Tenacity'    },
];

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function MeetingStatusBadge({ status }) {
  const map = {
    draft:     { cls: 'badge-yellow', label: 'Draft' },
    active:    { cls: 'badge-blue',   label: 'In Progress' },
    completed: { cls: 'badge-green',  label: 'Completed' },
  };
  const { cls, label } = map[status] || map.draft;
  return <span className={`badge ${cls}`}>{label}</span>;
}

const CHART_COLORS = ['#4ECDC4', '#FF6B6B', '#FFE66D', '#A8E6CF', '#C3B1E1', '#F7A072'];

function scoreColor(s) {
  if (s >= 4) return '#2d6a4f';
  if (s >= 3) return '#f4a261';
  return '#e63946';
}

function scoreLabel(s) {
  if (s >= 4.5) return { label: 'Excellent',       cls: 'badge-green'  };
  if (s >= 3.5) return { label: 'Good',             cls: 'badge-blue'   };
  if (s >= 2.5) return { label: 'Needs attention',  cls: 'badge-yellow' };
  return              { label: 'Critical',          cls: 'badge-red'    };
}

function computeScores(responses, categories) {
  const out = {};
  categories.forEach(cat => {
    const vals = [];
    responses.forEach(r => {
      cat.questions.forEach(q => {
        if (r.answers?.[q.id] != null) vals.push(r.answers[q.id]);
      });
    });
    out[cat.id] = vals.length
      ? +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2)
      : null;
  });
  return out;
}

// ─── Tab: Meetings ────────────────────────────────────────────────────────────

function MeetingsTab({ team, meetings }) {
  const navigate = useNavigate();

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
        <Link to={`/meetings/new?teamId=${team.id}`} className="btn btn-primary" style={{ fontSize: '0.85rem' }}>
          + Record New Meeting
        </Link>
      </div>

      {meetings.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>📋</div>
          <h3 style={{ fontFamily: 'var(--font-display)', marginBottom: 8 }}>No meetings yet</h3>
          <p style={{ color: 'var(--ink-muted)', marginBottom: 20 }}>Record your first observation for {team.name}.</p>
          <Link to={`/meetings/new?teamId=${team.id}`} className="btn btn-primary">Record first meeting →</Link>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {meetings.map(m => {
            const isComplete = ['completed', 'reviewed'].includes(m.status);
            const destination = isComplete ? `/meetings/${m.id}/report` : `/meetings/${m.id}/record`;
            return (
              <button
                key={m.id}
                onClick={() => navigate(destination)}
                style={{
                  width: '100%',
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '16px 20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  textAlign: 'left',
                  transition: 'var(--transition)',
                  boxShadow: 'var(--shadow-sm)',
                  fontFamily: 'var(--font-body)',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
              >
                {/* Name + date */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.name}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--ink-muted)', marginTop: 2 }}>
                    {formatDate(m.meetingDate)}
                  </div>
                </div>

                {/* Metrics — hidden on mobile */}
                <div className="hide-mobile" style={{ display: 'flex', gap: 20, flexShrink: 0 }}>
                  <div>
                    <div style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-muted)', fontWeight: 700, marginBottom: 2 }}>Attendees</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)' }}>{m.attendees?.length || 0}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-muted)', fontWeight: 700, marginBottom: 2 }}>Duration</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)' }}>{getMeetingDurationMs(m) ? formatDuration(getMeetingDurationMs(m)) : '—'}</div>
                  </div>
                </div>

                {/* Status + chevron */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                  <MeetingStatusBadge status={m.status} />
                  <span style={{ fontSize: '1.1rem', color: 'var(--ink-muted)' }}>›</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Tab: Working Genius ──────────────────────────────────────────────────────

function WGLetters({ geniuses, frustrations }) {
  return (
    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
      {WG_TYPES.map(g => {
        const isGenius = geniuses?.includes(g.key);
        const isFrustration = frustrations?.includes(g.key);
        return (
          <span key={g.key} title={g.label} style={{
            width: 24, height: 24, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.68rem', fontWeight: 800, flexShrink: 0,
            background: isGenius ? GC : isFrustration ? FC + '22' : 'transparent',
            color: isGenius ? 'white' : isFrustration ? FC : 'var(--border)',
            border: isFrustration ? `2px dashed ${FC}` : isGenius ? 'none' : '2px solid var(--border)',
          }}>{g.key}</span>
        );
      })}
    </div>
  );
}

function WorkingGeniusTab({ team }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDocs(query(collection(db, 'users'), where('teamId', '==', team.id)));
        setMembers(snap.docs.map(d => ({ uid: d.id, ...d.data() })).sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '')));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [team.id]);

  if (loading) return <PageLoading text="Loading Working Genius..." />;

  const membersWithProfiles = members.filter(m => (m.geniuses || []).length > 0 || (m.frustrations || []).length > 0);
  const geniusCounts = {};
  const frustrationCounts = {};
  WG_TYPES.forEach(g => { geniusCounts[g.key] = 0; frustrationCounts[g.key] = 0; });
  members.forEach(m => {
    (m.geniuses || []).forEach(k => { if (k in geniusCounts) geniusCounts[k]++; });
    (m.frustrations || []).forEach(k => { if (k in frustrationCounts) frustrationCounts[k]++; });
  });
  const maxG = Math.max(...Object.values(geniusCounts));
  const maxF = Math.max(...Object.values(frustrationCounts));
  const proneTo  = maxG > 0 ? WG_TYPES.filter(g => geniusCounts[g.key] === maxG)     : [];
  const averseTo = maxF > 0 ? WG_TYPES.filter(g => frustrationCounts[g.key] === maxF) : [];

  if (members.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 48 }}>
        <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>✦</div>
        <h3 style={{ fontFamily: 'var(--font-display)', marginBottom: 8 }}>No team members found</h3>
        <p style={{ color: 'var(--ink-muted)' }}>Members will appear here once they join the team.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Team Overview */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>👥</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--navy)' }}>Team Overview</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>{membersWithProfiles.length} of {members.length} members have set their profile</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {members.map((m, i) => {
            const hasProfile = (m.geniuses || []).length > 0 || (m.frustrations || []).length > 0;
            return (
              <div key={m.uid} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 4px', borderBottom: i < members.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ fontWeight: 600, fontSize: '0.875rem', minWidth: 110, color: 'var(--ink)' }}>{(m.displayName || 'Unknown').split(' ')[0]}</div>
                {hasProfile
                  ? <WGLetters geniuses={m.geniuses} frustrations={m.frustrations} />
                  : <>
                      {WG_TYPES.map(g => (
                        <span key={g.key} style={{ width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.68rem', fontWeight: 800, color: '#ccc', border: '2px solid #eee' }}>{g.key}</span>
                      ))}
                      <span style={{ fontSize: '0.72rem', color: 'var(--ink-muted)', marginLeft: 4 }}>Not set</span>
                    </>
                }
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 18, marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)', fontSize: '0.75rem', color: 'var(--ink-muted)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><div style={{ width: 12, height: 12, borderRadius: '50%', background: GC }} />Genius</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}><div style={{ width: 12, height: 12, borderRadius: '50%', border: `2px dashed ${FC}`, background: FC + '18' }} />Frustration</div>
        </div>
      </div>

      {/* Tendencies */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#e8f0fe', color: '#4361ee', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>⚖️</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--navy)' }}>Team Genius Tendencies</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>Most common genius strengths and frustrations</div>
          </div>
        </div>
        <div style={{ padding: '16px 0', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-muted)', marginBottom: 10 }}>PRONE TO</div>
          {proneTo.length > 0 ? (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {proneTo.map(g => (
                <div key={g.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: GC, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.78rem', fontWeight: 800 }}>{g.key}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.875rem' }}>{g.label}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--ink-muted)' }}>{geniusCounts[g.key]} member{geniusCounts[g.key] !== 1 ? 's' : ''}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : <div style={{ fontSize: '0.875rem', color: 'var(--ink-muted)', fontStyle: 'italic' }}>No clear tendency</div>}
        </div>
        <div style={{ paddingTop: 16 }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-muted)', marginBottom: 10 }}>AVERSE TO</div>
          {averseTo.length > 0 ? (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {averseTo.map(g => (
                <div key={g.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: FC + '22', border: `2px dashed ${FC}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.78rem', fontWeight: 800, color: FC }}>{g.key}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.875rem' }}>{g.label}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--ink-muted)' }}>{frustrationCounts[g.key]} member{frustrationCounts[g.key] !== 1 ? 's' : ''}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : <div style={{ fontSize: '0.875rem', color: 'var(--ink-muted)', fontStyle: 'italic' }}>No clear tendency</div>}
        </div>
      </div>

      {/* Genius Map — #9 fix: className="wg-map-grid" */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>👥</div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--navy)' }}>Team Working Genius Map</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>Visual map of team members' Working Genius profiles</div>
          </div>
        </div>
        {membersWithProfiles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--ink-muted)', fontSize: '0.875rem' }}>
            No team members have set their Working Genius profile yet.
          </div>
        ) : (
          <div className="wg-map-grid" style={{ display: 'grid', gap: 12 }}>
            {WG_TYPES.map(g => {
              const geniusMembers      = membersWithProfiles.filter(m => (m.geniuses || []).includes(g.key));
              const frustrationMembers = membersWithProfiles.filter(m => (m.frustrations || []).includes(g.key));
              return (
                <div key={g.key} style={{ borderRadius: 12, border: '1.5px solid var(--border)', padding: '14px', background: 'var(--bg-soft)', position: 'relative' }}>
                  <div style={{ position: 'absolute', top: 10, right: 10, width: 26, height: 26, borderRadius: '50%', background: 'var(--navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 800 }}>{g.key}</div>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--navy)', marginBottom: 10, paddingRight: 32 }}>{g.label}</div>
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '2px 7px', borderRadius: 100, background: GC + '22', color: GC, display: 'inline-block', marginBottom: 5 }}>Genius</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {geniusMembers.length > 0 ? geniusMembers.map(m => (
                        <span key={m.uid} style={{ fontSize: '0.72rem', fontWeight: 600, padding: '2px 7px', borderRadius: 100, background: GC + '18', color: GC, border: `1px solid ${GC}55` }}>{(m.displayName || '?').split(' ')[0]}</span>
                      )) : <span style={{ fontSize: '0.78rem', color: 'var(--ink-muted)' }}>—</span>}
                    </div>
                  </div>
                  <div>
                    <span style={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '2px 7px', borderRadius: 100, background: FC + '18', color: FC, display: 'inline-block', marginBottom: 5 }}>Frustration</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {frustrationMembers.length > 0 ? frustrationMembers.map(m => (
                        <span key={m.uid} style={{ fontSize: '0.72rem', fontWeight: 600, padding: '2px 7px', borderRadius: 100, background: FC + '18', color: FC, border: `1px solid ${FC}33` }}>{(m.displayName || '?').split(' ')[0]}</span>
                      )) : <span style={{ fontSize: '0.78rem', color: 'var(--ink-muted)' }}>—</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab: Results ─────────────────────────────────────────────────────────────

function ResultsTab({ team }) {
  const [responses, setResponses]   = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [openEnded, setOpenEnded]   = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDocs(query(collection(db, 'responses'), where('teamId', '==', team.id)));
        const data = snap.docs.map(d => d.data());
        setResponses(data);
        setOpenEnded(data.flatMap(r => Object.entries(r.openEnded || {}).filter(([, v]) => v?.trim()).map(([k, v]) => ({ key: k, text: v }))));
        if (team.customQuestions) setCategories(withCurrentLabels(team.customQuestions));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [team.id]);

  if (loading) return <PageLoading text="Loading results..." />;

  if (responses.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 48 }}>
        <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>📊</div>
        <h3 style={{ fontFamily: 'var(--font-display)', marginBottom: 8 }}>No results yet</h3>
        <p style={{ color: 'var(--ink-muted)' }}>Results will appear here once team members have submitted their assessments.</p>
      </div>
    );
  }

  const catScores    = computeScores(responses, categories);
  const scoreValues  = Object.values(catScores).filter(Boolean);
  const overallScore = +(scoreValues.reduce((a, b) => a + b, 0) / scoreValues.length).toFixed(2);
  const { label: overallLabel, cls: overallCls } = scoreLabel(overallScore);

  const radarData = categories.map(cat => ({ category: cat.label, score: catScores[cat.id] || 0, fullMark: 5 }));
  const barData = categories.map((cat, i) => ({ name: cat.label.split(' ').slice(0, 2).join(' '), score: catScores[cat.id] || 0, color: CHART_COLORS[i] }));
  const oeKeys = { strengths: 'Strengths', challenges: 'Challenges', suggestions: 'Suggestions' };

  return (
    <div>
      {/* Overall score */}
      <div className="results-overall-card" style={{ background: 'var(--ink)', borderRadius: 'var(--radius-lg)', padding: '32px 36px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 8 }}>Team Health Score</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '4rem', color: 'white', lineHeight: 1 }}>
            {overallScore}<span style={{ fontSize: '1.4rem', color: 'rgba(255,255,255,0.3)' }}>/5</span>
          </div>
        </div>
        <div>
          <span className={`badge ${overallCls}`} style={{ fontSize: '0.82rem', padding: '5px 14px' }}>{overallLabel}</span>
          <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.3)', marginTop: 10 }}>{responses.length} response{responses.length !== 1 ? 's' : ''} collected</div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid-2" style={{ marginBottom: 24 }}>
        <div className="card">
          <div className="card-title">Category Overview</div>
          <div className="card-subtitle">Radar across all dimensions</div>
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis dataKey="category" tick={{ fontSize: 11, fill: '#8888aa' }} />
              <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fontSize: 10 }} />
              <Radar name="Score" dataKey="score" stroke="#2d6a4f" fill="#2d6a4f" fillOpacity={0.25} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        <div className="card">
          <div className="card-title">Score by Category</div>
          <div className="card-subtitle">1 = very low · 5 = excellent</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={barData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0eee8" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#8888aa' }} />
              <YAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
              <Tooltip formatter={v => [v.toFixed(2), 'Score']} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="score" radius={[6, 6, 0, 0]}>
                {barData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Category breakdown */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-title">Category Breakdown</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 8 }}>
          {categories.map(cat => {
            const score = catScores[cat.id];
            const { label, cls } = scoreLabel(score || 0);
            const pct = score ? (score / 5) * 100 : 0;
            return (
              <div key={cat.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: cat.color }} />
                    <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>{cat.label}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className={`badge ${cls}`}>{label}</span>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: scoreColor(score || 0) }}>{score?.toFixed(1) || '—'}</span>
                  </div>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${pct}%`, background: cat.color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}

// ─── Tab: Observation ────────────────────────────────────────────────────────────────

const OBS_CAT_META = {
  simpleProcess:     { label: 'Simple Process',     color: '#2a3d5b', bg: '#2a3d5b18', emoji: '📋' },
  compellingContent: { label: 'Compelling Content', color: '#5f7942', bg: '#5f794218', emoji: '💡' },
  healthyBehaviors:  { label: 'Healthy Behaviors',  color: '#cba43a', bg: '#cba43a18', emoji: '💪' },
  rightGenius:       { label: 'Right Chemistry',    color: '#7209b7', bg: '#7209b718', emoji: '✨' },
};

function obsPctColor(pct) {
  if (pct >= 70) return 'var(--accent)';
  if (pct >= 40) return '#cba43a';
  return '#922b3f';
}

function obsPctLabel(pct) {
  if (pct >= 80) return 'Strong';
  if (pct >= 60) return 'Present';
  if (pct >= 40) return 'Developing';
  if (pct >= 20) return 'Emerging';
  return 'Not Evident';
}

function ObsScoreRing({ pct, size = 68, color }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--border)" strokeWidth={6} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={6}
        strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{ transition: 'stroke-dasharray 0.6s ease' }} />
      <text x={size/2} y={size/2+1} textAnchor="middle" dominantBaseline="middle"
        fontSize={13} fontWeight={700} fill={color}>{pct}%</text>
    </svg>
  );
}

function ObservationTab({ team }) {
  const [obsMeetings, setObsMeetings] = useState([]);
  const [surveyPcts, setSurveyPcts]   = useState(null);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDocs(query(
          collection(db, 'meetings'),
          where('teamId', '==', team.id),
          where('status', 'in', ['completed', 'reviewed']),
        ));
        const docs = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(m => m.inventoryChecks && Object.keys(m.inventoryChecks).length > 0)
          .sort((a, b) => {
            const da = a.meetingDate?.toDate ? a.meetingDate.toDate() : new Date(a.meetingDate || 0);
            const db2 = b.meetingDate?.toDate ? b.meetingDate.toDate() : new Date(b.meetingDate || 0);
            return db2 - da;
          })
          .slice(0, 5);

        const scored = docs.map((m, i) => {
          const prev = docs.slice(i + 1);
          const scores = scoreInventoryFromChecks(m.inventoryChecks || {}, prev);
          return { ...m, _scores: scores };
        });
        setObsMeetings(scored);

        // Load survey responses for perception comparison
        const rSnap = await getDocs(query(collection(db, 'responses'), where('teamId', '==', team.id)));
        const responses = rSnap.docs.map(d => d.data());
        const cats = withCurrentLabels(team.customQuestions);
        const pcts = {};
        cats.forEach(cat => {
          const vals = [];
          responses.forEach(r => cat.questions.forEach(q => {
            if (r.answers?.[q.id] != null) vals.push(r.answers[q.id]);
          }));
          pcts[cat.id] = vals.length
            ? Math.round(((vals.reduce((a, b) => a + b, 0) / vals.length) - 1) / 4 * 100)
            : null;
        });
        setSurveyPcts(pcts);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [team.id]);

  if (loading) return <PageLoading text="Loading observation data..." />;

  if (obsMeetings.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 48 }}>
        <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>📋</div>
        <h3 style={{ fontFamily: 'var(--font-display)', marginBottom: 8 }}>No observation data yet</h3>
        <p style={{ color: 'var(--ink-muted)' }}>Scores will appear once a consultant has completed the Meeting Inventory for at least one meeting.</p>
      </div>
    );
  }

  const aggregateScores = obsMeetings[0]?._scores;
  const overallPct = aggregateScores?.pct || 0;
  const catKeys = Object.keys(OBS_CAT_META);

  const radarData = catKeys.map(key => ({
    category: OBS_CAT_META[key].label,
    score: aggregateScores?.groups[key]?.pct || 0,
    fullMark: 100,
  }));

  const hasSurvey = surveyPcts && Object.values(surveyPcts).some(v => v !== null);
  const comparisonData = catKeys.map(key => ({
    category: OBS_CAT_META[key].label,
    'Team Perception': surveyPcts?.[key] ?? 0,
    'Consultant Observation': aggregateScores?.groups[key]?.pct ?? 0,
  }));
  const gaps = catKeys.map(key => {
    const p = surveyPcts?.[key] ?? 0;
    const o = aggregateScores?.groups[key]?.pct ?? 0;
    return { key, label: OBS_CAT_META[key].label, color: OBS_CAT_META[key].color, perception: p, observation: o, diff: p - o };
  }).sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));

  return (
    <div>
      {/* Overall ring + radar + legend */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, minWidth: 100 }}>
            <ObsScoreRing pct={overallPct} size={90} color={obsPctColor(overallPct)} />
            <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-muted)' }}>Overall</div>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: obsPctColor(overallPct), padding: '2px 10px', borderRadius: 100, background: obsPctColor(overallPct) + '18' }}>
              {obsPctLabel(overallPct)}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 200, height: 210 }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData}>
                <PolarGrid stroke="var(--border)" />
                <PolarAngleAxis dataKey="category" tick={{ fontSize: 10, fill: 'var(--ink-muted)', fontWeight: 500 }} />
                <Radar dataKey="score" stroke="#2a3d5b" fill="#2a3d5b" fillOpacity={0.18} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 130 }}>
            {catKeys.map(key => {
              const meta = OBS_CAT_META[key];
              const g = aggregateScores?.groups[key];
              return (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 9, height: 9, borderRadius: '50%', background: meta.color, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--ink)', lineHeight: 1.2 }}>{meta.label}</div>
                    <div style={{ fontSize: '0.68rem', color: obsPctColor(g?.pct || 0), fontWeight: 700 }}>{g?.pct || 0}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Category rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
        {catKeys.map(key => {
          const meta = OBS_CAT_META[key];
          const g = aggregateScores?.groups[key] || { score: 0, max: 0, pct: 0 };
          return (
            <div key={key} style={{ borderRadius: 10, border: `1.5px solid ${meta.color}44`, background: 'var(--bg-card)', padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <ObsScoreRing pct={g.pct} size={52} color={meta.color} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--navy)', marginBottom: 3 }}>{meta.emoji} {meta.label}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: meta.color, padding: '2px 8px', borderRadius: 100, background: meta.bg }}>
                      {obsPctLabel(g.pct)}
                    </span>
                    <span style={{ fontSize: '0.72rem', color: 'var(--ink-muted)' }}>
                      {g.score}/{g.max} pts · {obsMeetings.length} meeting{obsMeetings.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Perception vs Observation */}
      {hasSurvey && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title" style={{ marginBottom: 4 }}>Team Perception vs. Consultant Observation</div>
          <div className="card-subtitle" style={{ marginBottom: 20 }}>Self-assessment vs. observed — both on a 0–100% scale.</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={comparisonData} margin={{ top: 4, right: 12, left: -12, bottom: 0 }} barGap={4} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="category" tick={{ fontSize: 10, fill: 'var(--ink-muted)' }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 10, fill: 'var(--ink-muted)' }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(val, name) => [`${val}%`, name]} contentStyle={{ fontSize: '0.78rem', borderRadius: 8, border: '1px solid var(--border)' }} />
              <Legend wrapperStyle={{ fontSize: '0.75rem', paddingTop: 12 }} formatter={v => <span style={{ color: 'var(--ink-soft)' }}>{v}</span>} />
              <Bar dataKey="Team Perception" fill="#4361ee" radius={[4,4,0,0]} fillOpacity={0.85} />
              <Bar dataKey="Consultant Observation" fill="#e76f51" radius={[4,4,0,0]} fillOpacity={0.85} />
            </BarChart>
          </ResponsiveContainer>
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--ink-muted)', marginBottom: 10 }}>Gap Analysis</div>
            {gaps.map(g => {
              const gapAbs = Math.abs(g.diff);
              const overconfident = g.diff > 0;
              return (
                <div key={g.key} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 12px', borderRadius: 8, marginBottom: 5,
                  background: gapAbs >= 10 ? (overconfident ? 'rgba(230,57,70,0.05)' : 'rgba(45,106,79,0.05)') : 'var(--bg-soft)',
                  border: gapAbs >= 10 ? `1px solid ${overconfident ? '#e6394622' : '#2d6a4f22'}` : '1px solid transparent',
                }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: g.color, flexShrink: 0 }} />
                  <div style={{ flex: 1, fontSize: '0.8rem', fontWeight: 500 }}>{g.label}</div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0 }}>
                    <div style={{ textAlign: 'center', minWidth: 44 }}>
                      <div style={{ fontSize: '0.58rem', color: '#4361ee', fontWeight: 700, textTransform: 'uppercase', marginBottom: 1 }}>Team</div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#4361ee' }}>{g.perception}%</div>
                    </div>
                    <div style={{ textAlign: 'center', minWidth: 44 }}>
                      <div style={{ fontSize: '0.58rem', color: '#e76f51', fontWeight: 700, textTransform: 'uppercase', marginBottom: 1 }}>Observed</div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#e76f51' }}>{g.observation}%</div>
                    </div>
                    <div style={{ minWidth: 46, textAlign: 'center', padding: '3px 7px', borderRadius: 6, background: gapAbs < 5 ? 'var(--bg-soft)' : overconfident ? 'rgba(230,57,70,0.1)' : 'rgba(45,106,79,0.1)' }}>
                      <div style={{ fontSize: '0.58rem', color: 'var(--ink-muted)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 1 }}>Gap</div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 800, color: gapAbs < 5 ? 'var(--ink-muted)' : overconfident ? '#e63946' : '#2d6a4f' }}>
                        {g.diff > 0 ? '+' : ''}{g.diff}%
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--bg-soft)', borderRadius: 8, fontSize: '0.72rem', color: 'var(--ink-muted)', lineHeight: 1.7 }}>
            <span style={{ color: '#e63946', fontWeight: 700 }}>Positive gap</span> = team rates itself higher than observed · 
            <span style={{ color: '#2d6a4f', fontWeight: 700 }}>Negative gap</span> = consultant sees more than the team gives itself credit for
          </div>
        </div>
      )}

      {/* Meeting trend */}
      {obsMeetings.length > 1 && (
        <div className="card">
          <div className="card-title" style={{ marginBottom: 4 }}>Meeting Trend</div>
          <div className="card-subtitle" style={{ marginBottom: 12 }}>Scores across the last {obsMeetings.length} meetings</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <div style={{ minWidth: 56 }} />
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
              {catKeys.map(key => (
                <div key={key} style={{ fontSize: '0.62rem', fontWeight: 700, color: OBS_CAT_META[key].color, textAlign: 'center' }}>
                  {OBS_CAT_META[key].emoji}
                </div>
              ))}
            </div>
            <div style={{ minWidth: 32, fontSize: '0.62rem', color: 'var(--ink-muted)', fontWeight: 700, textAlign: 'right' }}>All</div>
          </div>
          {obsMeetings.map((m, i) => {
            const s = m._scores;
            const date = m.meetingDate?.toDate ? m.meetingDate.toDate() : m.meetingDate ? new Date(m.meetingDate) : null;
            const dateStr = date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : `Mtg ${obsMeetings.length - i}`;
            return (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--ink-muted)', minWidth: 56 }}>{dateStr}</div>
                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
                  {catKeys.map(key => {
                    const g = s?.groups[key];
                    return (
                      <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                        <div style={{ fontSize: '0.68rem', color: obsPctColor(g?.pct || 0), fontWeight: 700 }}>{g?.pct || 0}%</div>
                        <div style={{ width: '100%', height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${g?.pct || 0}%`, height: '100%', background: OBS_CAT_META[key].color, borderRadius: 3 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: obsPctColor(s?.pct || 0), minWidth: 32, textAlign: 'right' }}>{s?.pct || 0}%</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Tab: Team Assessment ─────────────────────────────────────────────────────

function TeamAssessmentTab({ team }) {
  return <TeamAssessment teamId={team.id} />;
}

// ─── Tab: Manage Team ─────────────────────────────────────────────────────────

function ManageTeamTab({ team, onUpdate }) {
  const { currentUser, userProfile } = useAuth();
  const [members, setMembers]             = useState([]);
  const [consultants, setConsultants]     = useState([]);
  const [allConsultants, setAllConsultants] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [newEmail, setNewEmail]           = useState('');
  const [inviting, setInviting]           = useState(false);
  const [message, setMessage]             = useState(null);
  const [removingEmail, setRemovingEmail] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [teamData, setTeamData]           = useState(team);
  const [selectedConsultant, setSelectedConsultant] = useState('');
  const [addingConsultant, setAddingConsultant]     = useState(false);
  const [sendingReminders, setSendingReminders]     = useState(false);
  const [sendingReminderTo, setSendingReminderTo]   = useState(null);
  const [resendingInvites, setResendingInvites]     = useState(false);
  const [resendingInviteTo, setResendingInviteTo]   = useState(null);

  useEffect(() => { loadData(); }, [team.id]);

  async function loadData() {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'teams', team.id));
      const td = { id: snap.id, ...snap.data() };
      let emails = td.memberEmails || [];

      // Self-heal: the leader should always be a member. Older teams (or ones
      // where the leader was later removed) can have leaderEmail missing from
      // memberEmails, which silently drops them from every member list.
      const leaderEmail = td.leaderEmail;
      if (leaderEmail && !emails.some(e => e.toLowerCase() === leaderEmail.toLowerCase())) {
        await updateDoc(doc(db, 'teams', team.id), { memberEmails: arrayUnion(leaderEmail) });
        emails = [...emails, leaderEmail];
        td.memberEmails = emails;
      }
      setTeamData(td);

      if (emails.length) {
        const usersSnap = await getDocs(
          query(collection(db, 'users'), where('teamId', '==', team.id))
        );
        const userMap = {};
        usersSnap.forEach(d => { userMap[(d.data().email || '').toLowerCase()] = { uid: d.id, ...d.data() }; });

        const responsesSnap = await getDocs(
          query(collection(db, 'responses'), where('teamId', '==', team.id))
        );
        const submittedIds = new Set(responsesSnap.docs.map(d => d.data().userId));

        const leaderKey = (leaderEmail || '').toLowerCase();
        const list = emails
          .map(email => {
            const u = userMap[email.toLowerCase()];
            return { email, registered: !!u, uid: u?.uid, name: u?.displayName || null, submitted: u?.uid ? submittedIds.has(u.uid) : false, isLeader: email.toLowerCase() === leaderKey };
          })
          .sort((a, b) => {
            if (a.isLeader !== b.isLeader) return a.isLeader ? -1 : 1;
            return (a.name || a.email).toLowerCase().localeCompare((b.name || b.email).toLowerCase());
          });
        setMembers(list);
      } else {
        setMembers([]);
      }

      // Load current consultants display names
      const cEmails = td.consultantEmails || [];
      if (cEmails.length) {
        const cSnap = await getDocs(
          query(collection(db, 'users'), where('email', 'in', cEmails.slice(0, 10)))
        );
        const cMap = {};
        cSnap.forEach(d => { cMap[d.data().email] = d.data().displayName || d.data().email; });
        setConsultants(cEmails.map(e => ({ email: e, name: cMap[e] || e })));
      } else {
        setConsultants([]);
      }

      // Load all consultants for dropdown
      const allCSnap = await getDocs(
        query(collection(db, 'users'), where('role', '==', 'consultant'))
      );
      setAllConsultants(allCSnap.docs.map(d => ({ email: d.data().email, name: d.data().displayName || d.data().email })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function sendInvite(email, role) {
    email = (email || '').trim().toLowerCase();
    setInviting(true);
    setMessage(null);
    try {
      const inviteRef = await addDoc(collection(db, 'invitations'), {
        email, role, teamId: team.id, teamName: teamData?.name,
        invitedBy: currentUser.uid, status: 'pending', createdAt: serverTimestamp(),
      });
      if (role === 'team_member') {
        await updateDoc(doc(db, 'teams', team.id), { memberEmails: arrayUnion(email) });
      }
      try {
        const sendInviteEmail = httpsCallable(functions, 'sendInviteEmail');
        await sendInviteEmail({ to: email, role, teamName: teamData?.name, inviteToken: inviteRef.id, inviterName: userProfile?.displayName });
      } catch (e) {
        console.warn('Email send failed:', e.message);
      }
      setMessage({ type: 'success', text: `Invitation sent to ${email}` });
      setNewEmail('');
      await loadData();
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to send invitation.' });
    } finally {
      setInviting(false);
    }
  }

  async function removeMember(email) {
    setRemovingEmail(email);
    try {
      await updateDoc(doc(db, 'teams', team.id), { memberEmails: arrayRemove(email) });
      setMembers(prev => prev.filter(m => m.email !== email));
      setMessage({ type: 'success', text: `${email} removed from team.` });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to remove member.' });
    } finally {
      setRemovingEmail(null);
      setConfirmRemove(null);
    }
  }

  async function sendReminders() {
    setSendingReminders(true);
    setMessage(null);
    try {
      const sendAssessmentReminder = httpsCallable(functions, 'sendAssessmentReminder');
      const result = await sendAssessmentReminder({ teamId: team.id });
      const sent = result.data?.sent ?? 0;
      setMessage({
        type: 'success',
        text: sent > 0 ? `Reminder sent to ${sent} member${sent === 1 ? '' : 's'}.` : 'Everyone has already completed the assessment.',
      });
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to send reminders. Please try again.' });
    } finally {
      setSendingReminders(false);
    }
  }

  async function sendReminderTo(email) {
    setSendingReminderTo(email);
    setMessage(null);
    try {
      const sendAssessmentReminder = httpsCallable(functions, 'sendAssessmentReminder');
      await sendAssessmentReminder({ teamId: team.id, email });
      setMessage({ type: 'success', text: `Reminder sent to ${email}.` });
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: `Failed to send reminder to ${email}.` });
    } finally {
      setSendingReminderTo(null);
    }
  }

  async function resendInviteEmailFor(email) {
    const inviteRef = await addDoc(collection(db, 'invitations'), {
      email, role: 'team_member', teamId: team.id, teamName: teamData?.name,
      invitedBy: currentUser.uid, status: 'pending', createdAt: serverTimestamp(),
    });
    const sendInviteEmail = httpsCallable(functions, 'sendInviteEmail');
    await sendInviteEmail({ to: email, role: 'team_member', teamName: teamData?.name, inviteToken: inviteRef.id, inviterName: userProfile?.displayName });
  }

  async function resendInvite(email) {
    setResendingInviteTo(email);
    setMessage(null);
    try {
      await resendInviteEmailFor(email);
      setMessage({ type: 'success', text: `Invitation resent to ${email}.` });
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: `Failed to resend invitation to ${email}.` });
    } finally {
      setResendingInviteTo(null);
    }
  }

  async function resendAllInvites() {
    const pending = members.filter(m => !m.registered).map(m => m.email);
    if (!pending.length) return;
    setResendingInvites(true);
    setMessage(null);
    try {
      await Promise.all(pending.map(resendInviteEmailFor));
      setMessage({ type: 'success', text: `Invitation resent to ${pending.length} member${pending.length === 1 ? '' : 's'}.` });
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to resend some invitations.' });
    } finally {
      setResendingInvites(false);
    }
  }

  async function addConsultant() {
    if (!selectedConsultant) return;
    setAddingConsultant(true);
    setMessage(null);
    try {
      await updateDoc(doc(db, 'teams', team.id), { consultantEmails: arrayUnion(selectedConsultant.toLowerCase()) });
      setMessage({ type: 'success', text: `${selectedConsultant} added as consultant.` });
      setSelectedConsultant('');
      await loadData();
      onUpdate();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to add consultant.' });
    } finally {
      setAddingConsultant(false);
    }
  }

  if (loading) return <PageLoading text="Loading team data…" />;

  const submittedCount = members.filter(m => m.submitted).length;
  const registeredCount = members.filter(m => m.registered).length;
  const pendingReminderCount = members.filter(m => m.registered && !m.submitted).length;
  const pendingInviteCount = members.filter(m => !m.registered).length;
  const availableConsultants = allConsultants.filter(c => {
    const existing = (teamData?.consultantEmails || []).map(e => e.toLowerCase());
    return !existing.includes((c.email || '').toLowerCase());
  });

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: 'var(--ink)' }}>Manage Team</div>
        <div style={{ fontSize: '0.85rem', color: 'var(--ink-muted)', marginTop: 4 }}>Manage members and track assessment progress.</div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 20 }}>
          {message.text}
        </div>
      )}

      <div className="grid-2" style={{ marginBottom: 28 }}>
        <div className="stat-card">
          <div className="stat-label">Team Members</div>
          <div className="stat-value">{members.length}</div>
          <div className="stat-sub">{registeredCount} registered</div>
          <button
            className="btn btn-secondary"
            style={{ marginTop: 12, padding: '6px 14px', fontSize: '0.8rem' }}
            onClick={resendAllInvites}
            disabled={resendingInvites || pendingInviteCount === 0}
            title={pendingInviteCount === 0 ? 'Everyone has registered' : undefined}
          >
            {resendingInvites ? <span className="spinner" /> : 'Resend invitation'}
          </button>
        </div>
        <div className="stat-card">
          <div className="stat-label">Assessments</div>
          <div className="stat-value">{submittedCount}</div>
          <div className="stat-sub">of {members.length} completed</div>
          <button
            className="btn btn-secondary"
            style={{ marginTop: 12, padding: '6px 14px', fontSize: '0.8rem' }}
            onClick={sendReminders}
            disabled={sendingReminders || pendingReminderCount === 0}
            title={pendingReminderCount === 0 ? 'Everyone has completed the assessment' : undefined}
          >
            {sendingReminders ? <span className="spinner" /> : 'Send reminder'}
          </button>
        </div>
      </div>

      {/* Team Members List */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-title">Team Members ({members.length})</div>
        <div className="card-subtitle">Team leader shown first, then alphabetically — check mark indicates assessment completed</div>

        {members.length === 0 ? (
          <p style={{ color: 'var(--ink-muted)', fontSize: '0.875rem', marginTop: 16 }}>No members yet. Use the invite form below to add your team.</p>
        ) : (
          <div style={{ marginTop: 16 }}>
            <div className="member-list-row" style={{ gap: '8px 16px', padding: '6px 12px', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-muted)', borderBottom: '1px solid var(--border)', marginBottom: 8 }}>
              <span>Name</span>
              <span className="member-email-col">Email</span>
              <span style={{ textAlign: 'center' }}>Assessment</span>
              <span></span>
            </div>
            {members.map(m => (
              <div key={m.email} className="member-list-row" style={{ gap: '8px 16px', alignItems: 'center', padding: '10px 12px', borderRadius: 8, transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-soft)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {m.name || <span style={{ color: 'var(--ink-muted)', fontStyle: 'italic', fontWeight: 400 }}>Not registered</span>}
                    {m.isLeader && (
                      <span style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 4, padding: '1px 6px' }}>
                        Team Leader
                      </span>
                    )}
                  </div>
                  {!m.registered && <div style={{ fontSize: '0.72rem', color: 'var(--gold)', marginTop: 2 }}>Invite pending</div>}
                  <div className="member-email-mobile" style={{ fontSize: '0.75rem', color: 'var(--ink-muted)', marginTop: 2 }}>{m.email}</div>
                </div>
                <div className="member-email-col" style={{ fontSize: '0.82rem', color: 'var(--ink-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email}</div>
                <div style={{ textAlign: 'center' }}>
                  {m.submitted
                    ? <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, background: 'var(--olive-light)', borderRadius: '50%', color: 'white', fontSize: '0.8rem', fontWeight: 700 }}>✓</span>
                    : <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, border: '2px solid var(--border)', borderRadius: '50%', color: 'var(--ink-muted)', fontSize: '0.75rem' }}>–</span>}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                  {confirmRemove === m.email ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={() => removeMember(m.email)} disabled={removingEmail === m.email}>{removingEmail === m.email ? '…' : 'Confirm'}</button>
                      <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.75rem' }} onClick={() => setConfirmRemove(null)}>Cancel</button>
                    </div>
                  ) : (
                    <>
                      {!m.registered && (
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                          onClick={() => resendInvite(m.email)}
                          disabled={resendingInviteTo === m.email}
                        >
                          {resendingInviteTo === m.email ? <span className="spinner" /> : 'Resend invitation'}
                        </button>
                      )}
                      {m.registered && !m.submitted && (
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                          onClick={() => sendReminderTo(m.email)}
                          disabled={sendingReminderTo === m.email}
                        >
                          {sendingReminderTo === m.email ? <span className="spinner" /> : 'Send reminder'}
                        </button>
                      )}
                      <button onClick={() => setConfirmRemove(m.email)} style={{ background: 'none', border: 'none', color: 'var(--ink-muted)', cursor: 'pointer', fontSize: '0.8rem', padding: '4px 8px', borderRadius: 6, transition: 'color 0.2s' }} onMouseEnter={e => e.currentTarget.style.color = 'var(--burgundy)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-muted)'}>Remove</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invite Team Members */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-title">Invite Team Members</div>
        <div className="card-subtitle">They'll receive an email with a link to register and take the assessment.</div>
        <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
          <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && newEmail && sendInvite(newEmail, 'team_member')} placeholder="colleague@company.com" style={{ flex: 1, padding: '12px 16px', border: '1.5px solid var(--border)', borderRadius: 'var(--radius)', fontFamily: 'var(--font-body)', fontSize: '0.95rem' }} />
          <button className="btn btn-primary" onClick={() => newEmail && sendInvite(newEmail, 'team_member')} disabled={inviting || !newEmail}>
            {inviting ? <span className="spinner" /> : 'Send invite →'}
          </button>
        </div>
      </div>

      {/* Add Consultant */}
      <div className="card">
        <div className="card-title">Consultants</div>
        <div className="card-subtitle">Consultants currently assigned to this team.</div>

        {consultants.length > 0 && (
          <div style={{ marginTop: 12, marginBottom: 16 }}>
            {consultants.map(c => (
              <div key={c.email} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 8, background: 'var(--bg-soft)', marginBottom: 6 }}>
                <div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>{c.name}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--ink-muted)' }}>{c.email}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {availableConsultants.length > 0 ? (
          <div style={{ marginTop: 12, paddingTop: consultants.length > 0 ? 12 : 0, borderTop: consultants.length > 0 ? '1px solid var(--border)' : 'none' }}>
            <div className="card-subtitle" style={{ marginBottom: 10 }}>Add another consultant</div>
            <div style={{ display: 'flex', gap: 12 }}>
              <select value={selectedConsultant} onChange={e => setSelectedConsultant(e.target.value)} style={{ flex: 1, padding: '12px 16px', border: '1.5px solid var(--border)', borderRadius: 'var(--radius)', fontFamily: 'var(--font-body)', fontSize: '0.95rem', background: 'var(--bg-card)', color: 'var(--ink)' }}>
                <option value="">Select a consultant…</option>
                {availableConsultants.map(c => (
                  <option key={c.email} value={c.email}>{c.name} ({c.email})</option>
                ))}
              </select>
              <button className="btn btn-secondary" onClick={addConsultant} disabled={addingConsultant || !selectedConsultant}>
                {addingConsultant ? <span className="spinner" /> : 'Add →'}
              </button>
            </div>
          </div>
        ) : (
          <p style={{ color: 'var(--ink-muted)', fontSize: '0.875rem', marginTop: 12 }}>No additional consultants available to add.</p>
        )}
      </div>
    </div>
  );
}

// ─── Main: ConsultantTeamHub ──────────────────────────────────────────────────

const TABS = [
  { id: 'meetings',        label: '📋 Meetings'        },
  { id: 'observations',   label: '📊 Observations'     },
  { id: 'team-assessment', label: '📊 Team Assessment' },
  { id: 'working-genius', label: '✦ Working Genius'    },
  { id: 'manage-team',    label: '⚙ Manage Team'       },
];

export default function ConsultantTeamHub() {
  const { teamId }                  = useParams();
  const { currentUser, userProfile } = useAuth();
  const navigate                    = useNavigate();

  const [team, setTeam]         = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [activeTab, setActiveTab] = useState('meetings');
  const [loading, setLoading]   = useState(true);

  useEffect(() => { loadData(); }, [teamId]);

  async function loadData() {
    try {
      const teamSnap = await getDoc(doc(db, 'teams', teamId));
      const isAdmin = userProfile?.role === 'admin';
      if (!teamSnap.exists()) return navigate(isAdmin ? '/admin' : '/meetings');
      const teamData = { id: teamSnap.id, ...teamSnap.data() };
      setTeam(teamData);

      const meetingsQuery = isAdmin
        ? query(collection(db, 'meetings'), where('teamId', '==', teamId))
        : query(collection(db, 'meetings'), where('teamId', '==', teamId), where('consultantId', '==', currentUser.uid));
      const meetSnap = await getDocs(meetingsQuery);
      setMeetings(
        meetSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <PageLoading text="Loading team..." />;
  if (!team)   return <p style={{ color: 'var(--ink-muted)' }}>Team not found.</p>;

  return (
    <div className="fade-up">
      <Link to={userProfile?.role === 'admin' ? '/admin' : '/meetings'} className="link" style={{ fontSize: '0.85rem', display: 'inline-block', marginBottom: 24 }}>← All teams</Link>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
        <div style={{ width: 52, height: 52, borderRadius: 14, background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '1.4rem', fontWeight: 700, flexShrink: 0 }}>
          {team.name?.charAt(0) || '?'}
        </div>
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--ink)', lineHeight: 1.2 }}>{team.name}</h2>
          <p style={{ color: 'var(--ink-muted)', fontSize: '0.85rem', marginTop: 2 }}>
            {team.memberEmails?.length || 0} members · {meetings.length} meeting{meetings.length !== 1 ? 's' : ''} recorded
          </p>
        </div>
      </div>

      {/* Tabs — #CTH3 fix: className="hub-tabs" */}
      <div className="hub-tabs" style={{ display: 'flex', gap: 6, marginBottom: 28, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ padding: '10px 20px', background: 'none', border: 'none', borderBottom: `2.5px solid ${activeTab === t.id ? 'var(--olive)' : 'transparent'}`, color: activeTab === t.id ? 'var(--olive)' : 'var(--ink-muted)', fontFamily: 'var(--font-body)', fontSize: '0.875rem', fontWeight: activeTab === t.id ? 700 : 500, cursor: 'pointer', transition: 'var(--transition)', marginBottom: -1, letterSpacing: '0.02em' }}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'meetings'        && <MeetingsTab team={team} meetings={meetings} />}
      {activeTab === 'observations'   && <ObservationTab team={team} />}
      {activeTab === 'team-assessment' && <TeamAssessmentTab team={team} />}
      {activeTab === 'working-genius' && <WorkingGeniusTab team={team} />}
      {activeTab === 'manage-team'    && <ManageTeamTab team={team} onUpdate={loadData} />}
    </div>
  );
}
